import React, { useEffect, useState, useRef } from 'react'
import { createUseStyles } from 'react-jss'
import { useParams, useSearchParams } from 'react-router-dom'
import { AxiosResponse } from 'axios'
import { useAtom } from 'jotai'
import { getInitials } from '../../../utils/initials'
import { colors } from '../../../theme/palette'
import { CircleText, CircleIcon2 } from '../../ui/Circle/Circle'
import { CardColumn, Row } from '../../ui/Card/CardColumn'
import { Typography } from '../../ui/Typography/Typography'
import {
    Foa,
    PersonalInfo,
    ContactLedger,
    Envelop,
    Edit,
} from '../../../assets/icons'
import { CardColumnContainer } from '../../ui/Card/CardColumnContainer'
import { getMemberWorkPlaces, getOwnMember } from '../../../api/members/members'
import { Member } from '../../../models/Member'
import { buildRow } from '../MyProfile'
import { formatDate } from '../../../utils/format'
import { EditMemberProfile } from '../../modules/EditMemberProfile'
import { CancelWorkplace } from '../../modules/CancelWorkplace'
import { AddMemberToWorkplace } from '../../modules/AddMemberToWorkplace'
import { baseButton } from '../../../theme/reuseables'
import { GmapsLink } from '../../ui/GmapsLink/GmapsLink'
import { Workplace } from '../../../models/Workplace'
import { Favorite } from '../../ui/Favorite'
import { BackLink } from '../../ui/BackLink'
import { L } from '../../providers/Lokalise'
import { EditMemberNotes } from '../../modules/notes/EditMemberNotes'
import { profileAtom } from '../../../api/profile/profile'
import { WorkplaceDropdown } from '../../modules/WorkplaceDropdown'
import breakpoints from '../../../theme/breakpoints'
import { Spinner } from '../../ui/Loaders/Spinner'
import { Contact } from '../../../models/Contact'
import { removeFavorite, setFavorite } from '../../../api/favorite'
import { ImitatorNoAccessWarning } from '../../modules/Imitator/ImitatorNoAccessWarning'
import { TransferMemberToOtherWorkplace } from '../../modules/TransferMemberToOtherWorkplace'
import { RemoveMemberFromOwnList } from '../../modules/RemoveMemberFromOwnLIst'
import { EditMemberFixedButton } from '../../ui/EditMemberFixedButton'
import { Button } from '../../ui/Button/Button'

export const buildEmailRow = (
    email: string | undefined,
    label: string,
    isImitator?: boolean,
    showModal?: boolean,
    setShowImitatorModal?: (show: boolean) => void
): Row => {
    return {
        label: '',
        value: '',
        useContent: true,
        content: email ? (
            <>
                <a
                    href={`mailto:${email}`}
                    onClick={(e) => {
                        if (isImitator) {
                            e.preventDefault()
                            setShowImitatorModal(true)
                        }
                    }}
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    <div>
                        <Typography
                            tag="h4"
                            size="XXS"
                            color=" rgba(0, 0, 0, 0.4)"
                        >
                            {label}
                        </Typography>
                        <Typography tag="p" size="XS">
                            {email || '-'}
                        </Typography>
                    </div>
                    <Envelop color={colors.green} width="40px" />
                </a>
                <ImitatorNoAccessWarning
                    show={showModal}
                    onClose={() => setShowImitatorModal(false)}
                />
            </>
        ) : (
            <>
                <div>
                    <Typography tag="h4" size="XXS" color=" rgba(0, 0, 0, 0.4)">
                        {label}
                    </Typography>
                    <Typography tag="p" size="XS">
                        {email || '-'}
                    </Typography>
                </div>
                <Envelop color={colors.green} width="40px" />
            </>
        ),
    }
}

const useStyles = createUseStyles({
    container: {
        color: colors.black,
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                gridTemplateColumns: '1fr 1fr 1fr',
                padding: '0 0 5rem',
            },
        },
    },
    profileHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem 0',
    },
    nameContainer: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '5rem',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '1.5rem',
    },
    headerActions: {
        padding: '2rem 0',
        display: 'flex',
        flexDirection: 'column',
    },
    edit: {
        display: 'flex',
        flexBasis: '50%',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    section: {
        marginBottom: '3rem',

        '& > hr': {
            opacity: '0.2',
            margin: '1.25rem 0',
        },
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
        },
    },
    buttons: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        '& button': {
            margin: '10px 0',
            width: '100%',
        },
    },
    editButton: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    iconWrapper: {
        position: 'relative',
        width: '90px',
        marginRight: '30px',
    },
    notes: {
        marginTop: 'auto',
    },
    fixedButtonContainer: {
        position: 'relative',
        width: '100vw',
        maxWidth: '400px',
        zIndex: 1,
        '& button': {
            width: '100%',
            padding: '15px',
            backgroundColor: colors.green,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow:
                '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 8px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.15)',
            '&:hover': {
                backgroundColor: colors.green600,
            },
        },
    },
    popupMenu: {
        position: 'fixed',
        bottom: '70px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '400px',
        maxWidth: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow:
            '0 8px 24px rgba(0, 0, 0, 0.25), 0 4px 8px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.15)',
        zIndex: 999,
        overflow: 'hidden',
        display: 'none',
        '&.active': {
            display: 'block',
        },
        '& ul': {
            margin: 0,
            padding: 0,
            listStyle: 'none',
        },
        '& li': {
            padding: '15px 20px',
            borderBottom: `1px solid ${colors.borderColorLight}`,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:last-child': {
                borderBottom: 'none',
            },
            // '&:hover': {
            //     backgroundColor: colors.borderColorLight,
            // },
            // '& svg': {
            //     marginRight: '12px',
            //     width: '20px',
            //     height: '20px',
            // },
        },
    },
})

const MemberProfile: React.FC = () => {
    const classes = useStyles()
    const params = useParams()
    const [data, _setData] = useState<Member | undefined>(undefined)
    const [showImitatorModal, setShowImitatorModal] = useState(false)
    const [backurl, setbackUrl] = useState('/')
    const [selectedWorkplace, setSelectedWorkplace] = useState<
        Workplace | undefined
    >(undefined)
    const [searchParams] = useSearchParams()
    const [authProfile] = useAtom(profileAtom)
    const [showMenu, setShowMenu] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLDivElement>(null)
    const [TRworkPlaces, setTRworkPlaces] = useState<Workplace[]>([])

    const init = async (id: string): Promise<AxiosResponse> => {
        const res = await getMemberWorkPlaces()
        setTRworkPlaces(res.workplaces)
        const response = await getOwnMember(id)
        const { data: profileData } = response
        const profile = Member.build(profileData)
        _setData(profile)
        return response
    }

    useEffect(() => {
        const { id } = params

        if (id) {
            init(id)

            if (searchParams.get('backUrl')) {
                setbackUrl(searchParams.get('backUrl'))
            }
        }
    }, [params, searchParams])

    useEffect(() => {
        if (data && data.primaryWorkPlace && !selectedWorkplace) {
            setSelectedWorkplace(data.primaryWorkPlace)
        }
    }, [data])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (
                menuRef.current &&
                buttonRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setShowMenu(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const getPositionName = (): string => {
        const id = data.primaryWorkPlace.postionNumber
        const group = data.positions.find((pos) => +pos.id === +id)
        return group.name || '-'
    }

    const getGroupName = (): string => {
        const id = data.primaryWorkPlace.postionNumber
        const group = data.positions.find((pos) => +pos.id === +id)
        return group.group.name || '-'
    }

    const getPersonalInfoRows = (member: Member): Row[] => {
        return [
            buildRow(
                formatDate(member.birthday),
                L('GENERISK_FORM_FØDSELDAG_LABEL')
            ),
            buildRow(
                member.age ? `${member.age} år` : '-',
                L('GENERISK_FORM_ALDER_LABEL')
            ),
        ]
    }

    const getWorkInfo = (workplace: Workplace): Row[] => {
        let customedGmapsUrl = ''
        if (workplace && workplace.address) {
            const {
                addressLine1,
                addressLine2,
                addressLine3,
                postDistrict,
                postNumber,
            } = workplace?.address

            customedGmapsUrl = `https://www.google.com/maps/search/?api=1&query=${addressLine1}+${addressLine2}+${addressLine3}%2C+${postNumber}+${postDistrict}`
        }

        const rows: Row[] = []

        if (customedGmapsUrl !== '') {
            rows.push({
                label: '',
                value: '',
                useContent: !!customedGmapsUrl,
                content: !!customedGmapsUrl && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Typography
                            tag="h4"
                            size="XXS"
                            color=" rgba(0, 0, 0, 0.4)"
                        >
                            Arbejdssted
                        </Typography>
                        <Typography
                            tag="p"
                            size="XS"
                            style={{ marginBottom: '10px' }}
                        >
                            {workplace.name}
                        </Typography>
                        <GmapsLink customedGmapsUrl={customedGmapsUrl} />
                    </div>
                ),
            })
        } else {
            rows.push(buildRow(workplace?.name, 'Arbejdssted'))
        }

        const defaults = [
            ...rows,
            ...[
                buildRow(getPositionName(), L('GENERISK_FORM_STILLING_LABEL')),
                buildRow(
                    workplace?.hoursPerWeek,
                    L('GENERISK_FORM_TIMETAL_LABEL')
                ),
                buildRow(getGroupName(), L('GENERISK_FORM_FAGGRUPPE_LABEL')),
            ],
        ]
        defaults.push({
            label: '',
            value: '',
            useContent: true,
            content: (
                <div className={classes.buttons}>
                    {data ? <AddMemberToWorkplace member={data} /> : null}
                    {data ? <CancelWorkplace member={data} /> : null}
                </div>
            ),
        })

        return defaults
    }

    const getContactInfoRows = (member: Member): Row[] => {
        let workContact: Contact | undefined

        if (selectedWorkplace) {
            workContact = member.contactInfoList.find(
                (wc) =>
                    wc.contactInfoType === 'Arbejde' &&
                    wc.workPlacesContact.number === selectedWorkplace.number
            )
        }

        return [
            buildRow(
                member.privateContact?.mobileShow ||
                    (member.privateContact?.isTelefonPrivate
                        ? 'Hemmeligt nummer'
                        : '-'),
                'Privat mobil'
            ),
            buildEmailRow(
                member.privateContact?.email,
                L('GENERISK_FORM_PRIVAT-EMAIL_LABEL'),
                authProfile.isImitator,
                showImitatorModal,
                setShowImitatorModal
            ),
            buildRow(
                workContact?.telephone ||
                    (workContact?.isTelefonPrivate ? 'Hemmeligt nummer' : '-'),
                L('GENERISK_FORM_ARBEDJSPLADS-MOBIL_LABEL')
            ),
            buildEmailRow(
                workContact?.email,
                L('GENERISK_FORM_ARBEJDSMAIL_LABEL'),
                authProfile.isImitator,
                showImitatorModal,
                setShowImitatorModal
            ),
        ]
    }

    const buildTOR = (member: Member): JSX.Element => {
        return (
            <div>
                <Typography tag="h4" size="XXS" color=" rgba(0, 0, 0, 0.4)">
                    {L('GENERISK_FORM_TILLIDSHVERV_LABEL')}
                </Typography>
                <>
                    {member.typeOfRepresentatives.map((type) => {
                        return (
                            <Typography tag="p" size="XS">
                                {type.name}
                            </Typography>
                        )
                    })}
                </>
            </div>
        )
    }

    const getMembershipInfoRows = (member: Member): Row[] => {
        return [
            buildRow(
                member.memberNumber,
                L('GENERISK_FORM_MEDLEMSNUMMER_LABEL')
            ),
            buildRow(
                member.membershipInfo.name,
                L('GENERISK_FORM_FOAAFDELING_LABEL')
            ),
            buildRow(
                formatDate(new Date(member.membershipInfo.date)),
                L('GENERISK_FORM_INDMELDELSESDATO_LABEL')
            ),
            {
                label: '',
                value: '',
                useContent: true,
                content: buildTOR(member),
            },
        ]
    }

    const onClickFavorite = async (member: Member): Promise<void> => {
        const callback = member.isFavorite ? removeFavorite : setFavorite

        const response = await callback(`${member.memberId}`, 'MedlemIdent')

        if (response) {
            const { id } = params
            if (id) {
                init(id)
            }
        }
    }

    if (!data) return <Spinner />

    return (
        <div className={`${classes.container} page-up`}>
            <div className={classes.header}>
                <div>
                    <BackLink
                        to={backurl}
                        label={
                            backurl === '/'
                                ? L('MINEMEDLEMMER_DETALJE_TILBAGEKNAP_TITEL')
                                : 'Mine arbejdssteder'
                        }
                    />
                    <div className={classes.profileHeader}>
                        <div className={classes.nameContainer}>
                            <div className={classes.iconWrapper}>
                                <CircleIcon2
                                    width="92px"
                                    withBorder
                                    borderWidth="6px"
                                >
                                    <CircleText fontSize="30px">
                                        {getInitials(data.fullName)}
                                    </CircleText>
                                </CircleIcon2>
                                <Favorite
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        onClickFavorite(data)
                                    }}
                                    active={data.isFavorite}
                                    size={24}
                                    right="-15px"
                                    bottom="-2px"
                                />
                            </div>

                            <Typography tag="h1" size="XXXXL" spacing=".2rem">
                                {data.fullName}
                            </Typography>
                        </div>
                    </div>
                </div>
                <div className={classes.headerActions}>
                    <EditMemberNotes member={data} />
                </div>
            </div>
            <div className={classes.section}>
                <hr />
                <CardColumnContainer>
                    <div>
                        <CardColumn
                            titleIcon={<PersonalInfo />}
                            titleLabel={L(
                                'MINEMEDLEMMER_DETALJE_PERSONLIGEOPLYSNINGER-TITEL'
                            )}
                            rows={getPersonalInfoRows(data)}
                            key="Personlige oplysninger"
                        />
                        <CardColumn
                            titleIcon={<Foa />}
                            titleLabel={L(
                                'MINEMEDLEMMER_DETALJE_FOAMEDLEMSSKAB-TITEL'
                            )}
                            rows={getMembershipInfoRows(data)}
                            key="FOA medlemsskab"
                        />
                    </div>
                    {/* <CardColumn
                        titleIcon={<SuitcaseFill />}
                        titleLabel={L('GENERISK_FORM_ANSÆTTELSE_TITEL')}
                        rows={getWorkInfo(data.primaryWorkPlace)}
                        key={data.primaryWorkPlace?.address?.id}
                    /> */}

                    {selectedWorkplace ? (
                        <WorkplaceDropdown
                            showHo
                            selectedWorkplace={selectedWorkplace}
                            workplaces={data.workPlaces}
                            profile={data}
                            setSelectedWorkplace={setSelectedWorkplace}
                        />
                    ) : null}
                    <CardColumn
                        iconWidth="16px"
                        titleIcon={<ContactLedger />}
                        titleLabel={L('MINEMEDLEMMER_DETALJE_KONTAKT-TITEL')}
                        rows={getContactInfoRows(data)}
                        key="Kontakt"
                    />
                </CardColumnContainer>
            </div>
            <EditMemberFixedButton>
                <div className={classes.fixedButtonContainer} ref={buttonRef}>
                    <Button
                        onClick={() => setShowMenu(!showMenu)}
                        icon={<Edit fill="#fff" />}
                        size="large"
                    >
                        {L('REDIGER_MEDLEM_KNAP')}
                    </Button>
                </div>
                <div
                    className={`${classes.popupMenu} ${
                        showMenu ? 'active' : 'hidden'
                    }`}
                    ref={menuRef}
                >
                    <ul>
                        <li>
                            <EditMemberProfile
                                member={data}
                                onOpen={() => {
                                    setShowMenu(false)
                                }}
                                onUpdated={(member) =>
                                    init(String(member.memberId))
                                }
                            />
                        </li>
                        {TRworkPlaces && TRworkPlaces.length > 1 ? (
                            <li>
                                <TransferMemberToOtherWorkplace
                                    member={data}
                                    onOpen={() => {
                                        setShowMenu(false)
                                    }}
                                />
                            </li>
                        ) : null}
                        {TRworkPlaces && TRworkPlaces.length > 1 ? (
                            <li>
                                {data ? (
                                    <AddMemberToWorkplace
                                        member={data}
                                        onOpen={() => {
                                            setShowMenu(false)
                                        }}
                                    />
                                ) : null}
                            </li>
                        ) : null}
                        <li>
                            <RemoveMemberFromOwnList
                                member={data}
                                onOpen={() => {
                                    setShowMenu(false)
                                }}
                            />
                        </li>
                    </ul>
                </div>
            </EditMemberFixedButton>
        </div>
    )
}

export { MemberProfile }
